import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { AiOutlineClose, AiOutlineMenu } from "react-icons/ai";
import { FaMobile, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "react-i18next";

const NAV_LINKS = [
  { label: "navbar.nav-text1", to: "/" },
  { label: "navbar.nav-text2", to: "/expertises", section: "expertises" },
  { label: "navbar.nav-text3", to: "/resources", section: "resources" },
  { label: "navbar.nav-text4", to: "/projects", section: "projects" },
  { label: "navbar.nav-text5", to: "/a-propos-de-nous", section: "a-propos-de-nous" },
];

const PHONE_NUMBER = "+236 72 96 55 55";
const PHONE_LINK = "tel:+23672965555";
const EMAIL_ADDRESS = "contact@decintech.com";
const EMAIL_LINK = "mailto:contact@decintech.com";
const ADDRESS = "Rond point Pk0, Bangui (Centrafrique)";
const ADDRESS_LINK = "https://maps.app.goo.gl/u7yxgVFJr7LyzP9u5";

const Navbar = () => {
  const { t } = useTranslation();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 1000, easing: "ease-out-cubic", once: true });
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setNavOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    if (navOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navOpen]);

  const handleNavToggle = () => setNavOpen((prev) => !prev);

  const handleNavClick = (to, section) => (e) => {
    setNavOpen(false);

    if (to === "/" && !section) {
      if (location.pathname !== "/") {
        e.preventDefault();
        navigate("/");
      } else {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (section && location.pathname === "/") {
      e.preventDefault();
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      window.history.replaceState(null, "", `/${section}`);
      return;
    }

    if (section) {
      e.preventDefault();
      navigate(`/${section}`);
      return;
    }
  };

  return (
    <header className="fixed top-0 left-0 w-full bg-[#00171f] z-50 shadow-lg">
      <nav className="max-w-[1280px] mx-auto flex items-center justify-between h-20 lg:h-28 px-4">
        <h1 className="text-2xl font-bold text-[#00df9a]">
          <NavLink
            to="/"
            onClick={handleNavClick("/", null)}
            tabIndex={0}
            className="focus:outline-none"
          >
            Decintech
          </NavLink>
        </h1>

        <ul className="hidden md:flex text-base gap-2 items-center text-white">
          {NAV_LINKS.map(({ label, to, section }) => (
            <li key={label}>
              <NavLink
                to={to}
                onClick={handleNavClick(to, section)}
                className={({ isActive }) =>
                  `p-4 transition-colors ${
                    isActive ? "text-[#00df9a]" : "text-white"
                  } hover:text-[#00df9a]`
                }
                tabIndex={0}
              >
                {t(label)}
              </NavLink>
            </li>
          ))}

          <li className="p-4 hidden lg:flex flex-col items-start gap-2">
            <a
              href={PHONE_LINK}
              className="hover:text-[#00df9a] flex items-center  gap-2"
              aria-label={`Call us at ${PHONE_NUMBER}`}
            >
              <FaMobile />
              <span className="text-sm">{PHONE_NUMBER}</span>
            </a>
            <a
              href={EMAIL_LINK}
              className="hover:text-[#00df9a] flex items-center gap-2"
              aria-label={`Email us at ${EMAIL_ADDRESS}`}
            >
              <FaEnvelope />
              <span className="text-sm">{EMAIL_ADDRESS}</span>
            </a>
            <a
              href={ADDRESS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#00df9a] flex items-center gap-2"
              aria-label={`Find us at ${ADDRESS}`}
            >
              <FaMapMarkerAlt className="text-red-600" />
              <span className="text-sm">{ADDRESS}</span>
            </a>
          </li>
          <li className="p-4">
            <LanguageSwitcher />
          </li>
        </ul>

        <div className="md:hidden flex items-center gap-2 text-white">
          <LanguageSwitcher />
          <button
            aria-label={navOpen ? "Close menu" : "Open menu"}
            onClick={handleNavToggle}
            className="ml-2 focus:outline-none"
          >
            {navOpen ? <AiOutlineClose size={24} /> : <AiOutlineMenu size={24} />}
          </button>
        </div>

        {/* Mobile Menu: From Bottom to Top */}
        <div
          ref={menuRef}
          className={`fixed bottom-0 left-0 w-full bg-[#00171f] z-50 shadow-t-lg transform transition-transform duration-300 md:hidden
            ${navOpen ? "translate-y-0" : "translate-y-full"}`}
          tabIndex={-1}
          aria-hidden={!navOpen}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            {/* <h2 className="text-2xl font-bold text-[#00df9a]">
              <NavLink
                to="/"
                onClick={handleNavClick("/", null)}
                tabIndex={0}
                className="focus:outline-none"
              >
                Decintech
              </NavLink>
            </h2> */}
            <button onClick={handleNavToggle} aria-label="Close menu">
              <AiOutlineClose size={24} className="text-white" />
            </button>
          </div>

          <ul className="flex flex-col text-base  gap-2 mt-4 px-4 pb-6">
            {NAV_LINKS.map(({ label, to, section }) => (
              <li key={label}>
                <NavLink
                  to={to}
                  onClick={handleNavClick(to, section)}
                  className="block py-1 text-lg text-white hover:text-[#00df9a] transition-colors text-center"
                  tabIndex={navOpen ? 0 : -1}
                >
                  {t(label)}
                </NavLink>
              </li>
            ))}

            <li className="mt-6 flex flex-col gap-4 items-center text-center">
              <a
                href={PHONE_LINK}
                className="flex items-center gap-2 text-white hover:text-[#00df9a]"
                aria-label={`Call us at ${PHONE_NUMBER}`}
              >
                <FaMobile />
                <span className="text-sm">{PHONE_NUMBER}</span>
              </a>
              <a
                href={EMAIL_LINK}
                className="flex items-center gap-2 text-white hover:text-[#00df9a]"
                aria-label={`Email us at ${EMAIL_ADDRESS}`}
              >
                <FaEnvelope />
                <span className="text-sm">{EMAIL_ADDRESS}</span>
              </a>
              <a
                href={ADDRESS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white hover:text-[#00df9a]"
                aria-label={`Find us at ${ADDRESS}`}
              >
                <FaMapMarkerAlt className="text-red-600" />
                <span className="text-sm">{ADDRESS}</span>
              </a>
            </li>
          </ul>
        </div>

        {navOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={handleNavToggle}
            aria-hidden="true"
          />
        )}
      </nav>
    </header>
  );
};

export default Navbar;
